import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, ExterneReferenz, MonitorKarte, MonitorZustand } from "@kundeninfo/api-client";
import { formatDatum } from "../../format.js";
import { monitorSprungUrl, ZUSTAND_LABEL, ZUSTAND_RANG, ZUSTAND_STATUS_ART } from "../../monitor.js";
import { MONITOR_TEXT, TILE_TEXT } from "./texts.js";
import { TileFrame } from "./TileView.js";
import type { TileRowView } from "./tileCatalog.js";

/*
 * Die Monitor-Kachel der Übersicht (Entscheid 422): eine Zeile je zugeordnetem Portal-Mandanten mit dem Zustand aus dem Monitor.
 *
 * Sie lädt für sich (die Kundenseite wartet nie auf den Monitor), und ein Fehlschlag bleibt in seiner Zeile. Nachgerechnet wird
 * nichts: Zustand und Stand sind, was der Monitor geliefert hat; angeordnet wird nach der Monitor-Rangfolge, Schlechtestes oben,
 * und was nicht geprüft ist, ist nie grün.
 */

/** Nur die zwei Klientenwege, die diese Kachel braucht, damit sie sich im Test mit zwei `vi.fn` zeichnen lässt. */
export type MonitorKartenClient = Pick<ApiClient, "listExternalReferences" | "monitorKarte">;

/** Die Zuordnung eines Mandanten und, sobald sie da ist, seine Karte. */
export type MonitorEntry = {
  referenz: ExterneReferenz;
  /** `null` heißt „lädt noch“ und ist von „geladen, aber nichts zu zeigen“ zu unterscheiden. */
  karte: MonitorKarte | null;
  /** Der Abruf ist gescheitert, bevor der Server antworten konnte (Netz, 404 auf die Zuordnung); bleibt lokal in dieser Zeile. */
  fehler: string | null;
};

/**
 * Der Zustand, nach dem ein Eintrag einsortiert wird. Ohne Daten (lädt noch, nicht angebunden, nicht im Monitor, kaputt) ist er
 * `UNKNOWN` und steht damit über allem Grünen: Was niemand geprüft hat, darf nicht unter den geprüften guten Fällen verschwinden.
 */
export const entryState = (e: MonitorEntry): MonitorZustand => e.karte?.daten?.state ?? "UNKNOWN";

/** Schlechtestes oben nach `ZUSTAND_RANG`; gleiche Zustände behalten ihre Reihenfolge. */
export const worstFirst = (entries: readonly MonitorEntry[]): MonitorEntry[] =>
  [...entries].sort((a, b) => ZUSTAND_RANG[entryState(a)] - ZUSTAND_RANG[entryState(b)]);

/**
 * Lädt die Mandanten eines Kunden und je Mandant seine Monitor-Karte.
 *
 * Kein Zustand überlebt den Kontextwechsel: Beim Kundenwechsel fällt alles weg, bei `reload` steht der alte Stand, bis die neue
 * Liste da ist, und in beiden Fällen wird jede noch fliegende Antwort verworfen. Die Karten treffen nacheinander ein, ein Abruf
 * über ein langsames Netz kann noch unterwegs sein, wenn längst ein anderer Kunde dasteht; ohne die Laufnummer stünde dessen Lage
 * in der Kachel des neuen, und sie sähe plausibel aus.
 */
export function useMonitorEntries(client: MonitorKartenClient, customerId: string): {
  /** `null` heißt „noch nicht geladen“ und ist von „geladen und leer“ zu unterscheiden. */
  entries: MonitorEntry[] | null; baseUrl: string | null; loadError: string | null; reload: () => void;
} {
  const [entries, setEntries] = useState<MonitorEntry[] | null>(null);
  const [baseUrl, setBaseUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [again, setAgain] = useState(0);
  const reload = useCallback(() => setAgain((n) => n + 1), []);
  const run = useRef(0);
  const shownFor = useRef<string | null>(null);
  const listExternalReferences = client.listExternalReferences;
  const monitorKarte = client.monitorKarte;

  useEffect(() => {
    const mine = ++run.current;
    const stillCurrent = (): boolean => mine === run.current;
    const patch = (id: string, change: Partial<MonitorEntry>): void => setEntries((before) =>
      before === null ? before : before.map((e) => (e.referenz.id === id ? { ...e, ...change } : e)));
    // Beim Kundenwechsel fällt alles weg; beim Neuladen bleibt der alte Stand stehen, bis die neue Liste da ist (Spec „Leistung“).
    if (shownFor.current !== customerId) {
      setEntries(null);
      setBaseUrl(null);
      setLoadError(null);
    }
    shownFor.current = customerId;

    listExternalReferences(customerId)
      .then((answer) => {
        if (!stillCurrent()) return;
        const items = answer.items;
        const base = answer.monitorBasisUrl ?? null;
        setLoadError(null);
        setBaseUrl(base);
        setEntries(items.map((referenz) => ({ referenz, karte: null, fehler: null })));
        // Ohne eingerichteten Monitor wird nichts gerufen: Die Antwort stünde fest (`NICHT_ANGEBUNDEN`), ein Abruf je Mandant wäre Lärm.
        if (base === null) return;
        // Je Mandant ein eigener Abruf, nebeneinander: Ein langsamer Mandant hält die übrigen nicht auf. Jede Antwort schreibt nur
        // ihre eigene Zeile, gesucht über die Kennung der Zuordnung und nicht über den Listenplatz.
        for (const referenz of items) {
          monitorKarte(customerId, referenz.id)
            .then((karte) => { if (stillCurrent()) patch(referenz.id, { karte }); })
            // Der Fehler bleibt lokal in dieser Zeile; die übrigen und der Rest der Akte merken nichts davon.
            .catch(() => { if (stillCurrent()) patch(referenz.id, { fehler: MONITOR_TEXT.cardFailed }); });
        }
      })
      .catch(() => {
        if (!stillCurrent()) return;
        setEntries([]);
        setLoadError(MONITOR_TEXT.loadFailed);
      });

    // Auch das Abräumen zählt hoch: Beim Ausbauen sind alle fliegenden Antworten ungültig.
    return () => { run.current += 1; };
  }, [listExternalReferences, monitorKarte, customerId, again]);

  return { entries, baseUrl, loadError, reload };
}

/**
 * Höchstens so viele Zeilen zeigt eine Kachel, der Rest steht hinter „weitere“: wie `ROWS_PER_TILE` der Domäne, das der Vertrag als
 * `maxItems` von `TileSection.rows` nennt (web importiert die Domäne nicht; customerOverviewContract.test.ts hält beide gleich).
 */
export const MAX_TILE_ROWS = 3;

/**
 * Warum zu einem Mandanten keine Daten da sind, in einem Satz. Eine Karte mit Lage `ANGEZEIGT` und ohne Daten liefert der Server nie
 * (der Vertrag lässt es nur zu, weil `daten` nullbar ist); sie fiele hier auf „wird geladen …“ durch.
 */
function noDataText(e: MonitorEntry, baseUrl: string | null): string {
  if (e.karte !== null && e.karte.lage !== "ANGEZEIGT") return MONITOR_TEXT.lage[e.karte.lage];
  if (e.fehler !== null) return e.fehler;
  return baseUrl === null ? MONITOR_TEXT.notConnected : MONITOR_TEXT.loading;
}

/** Die Zeile eines Mandanten; der Zustand steht immer auch als Wort da, grün trägt keine Status-Art. */
function rowView(e: MonitorEntry, baseUrl: string | null): TileRowView {
  const state = entryState(e);
  const art = ZUSTAND_STATUS_ART[state];
  const data = e.karte?.daten ?? null;
  return { text: MONITOR_TEXT.rowName(e.referenz.externalId), kennung: e.referenz.systemKey,
    ...(data?.capturedAt ? { zusatz: formatDatum(data.capturedAt) } : {}),
    unter: data === null ? noDataText(e, baseUrl) : MONITOR_TEXT.openFindings(data.befunde.length),
    ...(art === "ok" ? {} : { statusArt: art }), status: ZUSTAND_LABEL[state] };
}

export type MonitorTileProps = {
  entries: readonly MonitorEntry[]; baseUrl: string | null; loadError: string | null; hidden: boolean;
  /** Der Kopf führt in die Stammdaten (Portal-Mandanten). */
  onHead: () => void;
  onRetry: () => void;
};

export function MonitorTile(p: MonitorTileProps): ReactElement {
  /* Die Zeile öffnet den Monitor in einem neuen Fenster; `noopener, noreferrer`, weil die Adresse dieser Seite die Kundenkennung trägt.
     Ohne Basisadresse gibt es keinen Verweis, und eine erfundene wäre schlimmer als keiner. */
  const open = (e: MonitorEntry): void => {
    const url = monitorSprungUrl(p.baseUrl, e.referenz.externalId);
    if (url !== null) window.open(url, "_blank", "noopener,noreferrer");
  };
  const sorted = worstFirst(p.entries);
  const hidden = sorted.length - MAX_TILE_ROWS;
  return <TileFrame tileKey="monitor" hidden={p.hidden} onHead={p.onHead} onRetry={p.onRetry}
    content={p.loadError !== null ? { error: p.loadError } : { count: sorted.length, empty: TILE_TEXT.monitor.empty(0), facts: [],
      rows: sorted.slice(0, MAX_TILE_ROWS).map((e) => ({ id: e.referenz.id, view: rowView(e, p.baseUrl), onSelect: () => open(e) })),
      ...(hidden > 0 ? { more: { count: hidden, text: TILE_TEXT.monitor.more(sorted.length, 0) } } : {}) }} />;
}
